export const USE_OF_FUNDS_OPTIONS = [
  { value: 'research_and_development', label: 'Research and development' },
  { value: 'hiring',              label: 'Hiring' },
  { value: 'marketing_sales',     label: 'Marketing / sales' },
  { value: 'production_and_operations',          label: 'Production and Operations' },
  { value: 'pilot_program',       label: 'Pilot program' },
  { value: 'equipment_and_materials', label: 'Equipment and materials' },
  { value: 'customer_discovery_and_validation', label: 'Customer discovery and validation' },
  { value: 'other',              label: 'Other' },
];
// research and development
// hiring 
// marketing and sales
// production and operations
// pilot program
// equipment and materials
// customer discovery and validation
// other


export function calcMetrics(answers) {
  const raise = parseFloat(answers.raise_amount) || 0;
  const check = parseFloat(answers.min_check_size) || 1;
  const scalingFactor = check < 25000 ? 40 : check <= 50000 ? 35 : 30;
  const numChecks = Math.ceil(raise / check);
  const totalContacts = scalingFactor * numChecks;
  const monthlyContacts = Math.min(Math.ceil(totalContacts / 3), 60);
  const weeklyContacts = Math.min(Math.ceil(monthlyContacts / 4), 15);
  return { numChecks, totalContacts, monthlyContacts, weeklyContacts };
}

export function fmt(n) { return n.toLocaleString(); }

export function fmtMoney(n) {
  if (!n && n !== 0) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

export function buildPlanStages(answers) {
  const m = calcMetrics(answers);
  return [
    {
      title: 'Stage 1 · Months 1–3',
      subtitle: 'People buy from people they like and trust. Create that condition before you need anything from them.',
      bullets: [
        `Target metrics: ${fmt(m.totalContacts)} warm intro targets · 3 events attended · ${fmt(m.totalContacts)} founder contacts made · 6 content pieces published`,
        `Attend a few local or sector startup event and collect ${fmt(m.monthlyContacts)} investor contacts. Do this monthly for 3 months.`,
        `Publish 6 pieces of blog posts, LinkedIn, or podcast appearances on the problem your company solves.`,
        `Build a target list of ${fmt(m.totalContacts)} investors. Score them: thesis fit (1–3), stage fit (1–3), warm intro path (yes/no). Only pursue investors scoring 5+ total.`,
        `Identify 4 portfolio companies and mutual connections connected with target investors and ask them for warm introductions.`,
        `Use Investor Hygiene to track your investor list.`,
      ],
    },
    {
      title: 'Stage 2 · Months 2–6',
      subtitle: 'Direct follow-up on the contacts you made in Stage 1. The goal of these meetings is to figure out whether the contact would even consider investing.',
      bullets: [
        `Pre-meeting email: "I'd like to spend 30 minutes sharing where [Company] is and understanding whether it fits your thesis. I'll be direct about where we are, and I'd love a clear yes or no at the end — no hard feelings either way. Does that work for you?"`,
        `Meeting opener: "My goal today is to give you a clear picture of [Company] and understand if it's the right fit for [Firm]. At the end, I'd love to know one of three things: you're interested, you're not a fit and why, or you need X before you can decide."`,
        `At the close of every meeting, explicitly ask: "What's our next step, and what's the timeline?" Do not leave without a defined next action or a clear no.`,
        `Send a same-day follow-up recapping the agreed next step, timeline, and any outstanding questions. Keep it under 5 lines.`,
      ],
    },
    {
      title: 'Stage 3 · Months 2–6',
      subtitle: 'Securing investors requires understanding their needs first.',
      bullets: [
        `"What does your ideal deal look like at this stage?" — surfaces ownership targets, check size range, and stage preference.`,
        `"What's missing from your portfolio that you're actively trying to fill?" — identify thesis gaps you might fill.`,
        `"What's made you pass on deals that looked similar to ours?" — surfaces objections before they become deal-killers.`,
        `"How do you think about [your market]?" — tests whether they're thinking about this space at all.`,
        `After each conversation, score the investor 1–5 in: thesis fit, urgency, decision authority. Drop anyone below 3 from active pursuit.`,
      ],
    },
    {
      title: 'Stage 4 · Months 2–6',
      subtitle: 'Confirm whether the investor can write the check you are looking for.',
      bullets: [
        `Confirm they are actively investing: "Are you actively deploying capital?"`,
        `Confirm typical check size. If they usually write ${fmtMoney(parseFloat(answers.min_check_size) * 0.5)} and you need ${fmtMoney(parseFloat(answers.min_check_size))} checks, they are a poor fit.`,
        `Ask about ownership requirements: "What's the minimum ownership that makes this worthwhile for you?"`,
      ],
    },
    {
      title: 'Stage 5 · Months 5–7',
      subtitle: "Map each investor's path to a final decision.",
      bullets: [
        `"What does a yes look like at your firm?" — map their decision path from first meeting to signed deal.`,
        `"Who else would need to be involved before a term sheet?" — identify all decision-makers.`,
        `"What's the typical timeline from first meeting to term sheet for you?" — if they say 6+ months and you need to close sooner, flag the conflict.`,
        `By month 7, you should be in conversations with decision-makers. If not, recalibrate your intro strategy.`,
        `Reference a close date in your conversations to create a sense of urgency.`,
      ],
    },
    {
      title: 'Stage 6 · Months 6–8',
      subtitle: 'Your pitch at this stage is not to generate interest, but to confirm fit and resolve remaining issues.',
      bullets: [
        `Open by referencing their stated pain: "Based on what you told me about your focus on [X], here's why I think [Company] is relevant."`,
        `Confirm fit at each section. After the problem slide: "Does this match with what you're seeing in the market?"`,
        `Address known objections early. You surfaced these in Stage 3 — proactively say: "I know the concern with companies like ours is usually [X]. Here's how we've thought about it."`,
        `Deck should be 10–12 slides: problem, solution, market, traction, team, business model, ask.`,
        `By month 9, complete formal partner meetings at ${m.numChecks * 4} firms and be in due diligence with at least ${Math.ceil(m.numChecks * 1.2)}.`,
      ],
    },
    {
      title: 'Stage 7 · Months 8–10',
      subtitle: 'Maintain consistent communication with investors to reinforce commitment.',
      bullets: [
        `Send a same-day recap after verbal commitment: agreed terms, timeline to term sheet, and a named next step.`,
        `Within 48 hours of verbal yes, send one piece of new positive information (a customer quote, a new metric, a reference from a founder they know).`,
        `Provide a clean data room covering: cap table, financials, legal, team, and key customer contracts. Target completing DD in 3 weeks.`,
        `Continue parallel conversations with follow-on investors while the lead is in DD. Having 2+ followers lined up gives the lead confidence.`,
        `Set a round close date and hold it: "We're closing on [Date], can we confirm your commitment by [1 week before Date]?"`,
        `After close: send a short note to every investor who passed, with context on what you raised and a door left open.`,
      ],
    },
  ];
}

export const planStyles = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-6)' },
  title: { fontFamily: 'var(--font-accent)', fontSize: 'var(--font-size-page-title)', fontWeight: 'var(--font-weight-page-title)', color: 'var(--color-rust)', lineHeight: 1.1, marginBottom: 4 },
  subtitle: { fontFamily: 'var(--font-body)', fontSize: 'var(--font-size-page-subtitle)', fontWeight: 'var(--font-weight-page-subtitle)', color: 'var(--color-forest)', opacity: 0.6 },
  editBtn: { padding: '8px 16px', borderRadius: 'var(--radius-sm)', background: 'none', border: '1.5px solid var(--color-mint-dark)', fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 700, color: 'var(--color-forest)', cursor: 'pointer', flexShrink: 0, marginTop: 4 },
  statsRow: { display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)', marginBottom: 'var(--space-4)' },
  stat: { background: 'var(--color-frost)', borderRadius: 'var(--radius-md)', padding: '12px 16px', textAlign: 'center', minWidth: 90, boxShadow: 'var(--shadow-card)' },
  statValue: { fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 800, color: 'var(--color-forest)', marginBottom: 2 },
  statLabel: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', opacity: 0.6, textTransform: 'uppercase', letterSpacing: '0.04em' },
  useRow: { display: 'flex', gap: 8, marginBottom: 'var(--space-6)', fontFamily: 'var(--font-body)', fontSize: 15 },
  useLabel: { color: 'var(--color-forest)', opacity: 0.6 },
  useValue: { color: 'var(--color-forest)', fontWeight: 700 },
  stage: { background: 'var(--color-frost)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-6)', marginBottom: 'var(--space-4)', boxShadow: 'var(--shadow-card)' },
  stageTitle: { fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 800, color: 'var(--color-forest)', marginBottom: 'var(--space-2)' },
  stageSubtitle: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', opacity: 0.7, lineHeight: 1.55, marginBottom: 'var(--space-4)', fontStyle: 'italic', fontWeight: 800 },
  bullets: { paddingLeft: 18, margin: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' },
  bullet: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', lineHeight: 1.6 },
};
